import React, { useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import {
  Table, Tag, Spin, Alert, Empty, Input, Select, Tooltip, Button, Popconfirm, message as antdMessage,
} from "antd";
import {
  SearchOutlined, PauseCircleOutlined, PlayCircleOutlined, ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import customClientAxios from "../customAxios";
import {
  AdminNurtureMessages,
  AdminNurtureQueueState,
  AdminNurtureQueuePause,
  AdminNurtureQueueResume,
} from "../utils/ApiEndpoints";

const Page = styled.div`
  padding: 24px 32px;
  background: #f8fafc;
  min-height: 100vh;
`;
const Header = styled.div`
  margin-bottom: 24px;
  h1 { font-size: 24px; font-weight: 700; color: #1e293b; margin: 0 0 4px; }
  p { color: #64748b; margin: 0; font-size: 14px; }
`;
const Filters = styled.div`
  background: white;
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr auto;
  gap: 12px;
  margin-bottom: 16px;
`;
const TableCard = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
`;
const QueueBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid ${(p) => (p.$paused ? "#fde68a" : "#bbf7d0")};
  background: ${(p) => (p.$paused ? "#fffbeb" : "#f0fdf4")};
  margin-bottom: 16px;
  .info { display: flex; align-items: center; gap: 12px; }
  .dot {
    width: 10px; height: 10px; border-radius: 50%;
    background: ${(p) => (p.$paused ? "#f59e0b" : "#22c55e")};
    box-shadow: 0 0 0 4px ${(p) => (p.$paused ? "#fef3c7" : "#dcfce7")};
  }
  .label { font-weight: 600; color: #1e293b; }
  .sub { color: #64748b; font-size: 12px; margin-top: 2px; }
  .counts { color: #475569; font-size: 12px; }
`;

const STATUS_COLOR = {
  queued: "default",
  sent: "blue",
  delivered: "geekblue",
  read: "purple",
  clicked: "success",
  failed: "error",
  skipped: "warning",
};

export default function NurtureMessages() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ stepDay: null, status: null, search: "" });
  const [queueState, setQueueState] = useState({ paused: false, counts: null });
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueBusy, setQueueBusy] = useState(false);

  const fetchQueueState = useCallback(async () => {
    setQueueLoading(true);
    try {
      const { data } = await customClientAxios().get(AdminNurtureQueueState);
      setQueueState(data?.data || { paused: false, counts: null });
    } catch (e) {
      // Non-blocking — keep page usable even if state read fails
      // (worker process may be down)
    } finally {
      setQueueLoading(false);
    }
  }, []);

  const handleTogglePause = useCallback(async () => {
    setQueueBusy(true);
    try {
      const url = queueState.paused ? AdminNurtureQueueResume : AdminNurtureQueuePause;
      const { data } = await customClientAxios().post(url);
      setQueueState(data?.data || { paused: !queueState.paused, counts: null });
      antdMessage.success(queueState.paused ? "Nurture pipeline resumed" : "Nurture pipeline paused");
    } catch (e) {
      antdMessage.error(e?.response?.data?.error?.message || e.message || "Failed");
    } finally {
      setQueueBusy(false);
    }
  }, [queueState.paused]);

  useEffect(() => { fetchQueueState(); }, [fetchQueueState]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (filters.stepDay) params.stepDay = filters.stepDay;
      if (filters.status) params.status = filters.status;
      if (filters.search?.trim()) params.search = filters.search.trim();
      const { data } = await customClientAxios().get(AdminNurtureMessages, { params });
      setRows(data?.data?.messages || []);
    } catch (e) {
      setError(e?.response?.data?.error?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const columns = [
    {
      title: "Lead",
      key: "lead",
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600, color: "#1e293b" }}>{r.leadName || r.leadId || "—"}</div>
          <div style={{ fontSize: 11, color: "#94a3b8" }}>{r.phoneE164}</div>
        </div>
      ),
    },
    { title: "Step", dataIndex: "stepDay", key: "stepDay", width: 70,
      render: (d) => d ? <Tag color="purple">D{d}</Tag> : "—" },
    { title: "Template", dataIndex: "templateKey", key: "templateKey", width: 180 },
    { title: "Status", dataIndex: "status", key: "status", width: 100,
      render: (s) => <Tag color={STATUS_COLOR[s] || "default"}>{s}</Tag> },
    { title: "Queued", dataIndex: "queuedAt", key: "queuedAt", width: 150,
      render: (d) => d ? dayjs(d).format("MMM D, HH:mm") : "—" },
    { title: "Sent", dataIndex: "sentAt", key: "sentAt", width: 150,
      render: (d) => d ? dayjs(d).format("MMM D, HH:mm") : "—" },
    { title: "Delivered", dataIndex: "deliveredAt", key: "deliveredAt", width: 150,
      render: (d) => d ? dayjs(d).format("MMM D, HH:mm") : "—" },
    { title: "Read", dataIndex: "readAt", key: "readAt", width: 150,
      render: (d) => d ? dayjs(d).format("MMM D, HH:mm") : "—" },
    { title: "Clicked", dataIndex: "clickedAt", key: "clickedAt", width: 150,
      render: (d) => d ? <Tag color="success">{dayjs(d).format("MMM D, HH:mm")}</Tag> : "—" },
    { title: "Failure", dataIndex: "failureReason", key: "failureReason",
      render: (r) => r ? <Tooltip title={r}><Tag color="error">view</Tag></Tooltip> : "—" },
  ];

  return (
    <Page>
      <Header>
        <h1>Nurture Messages</h1>
        <p>Every WhatsApp message sent to a prospect, with delivery and click-tracking timeline. Updates in real-time as msg91 webhooks land.</p>
      </Header>

      <QueueBanner $paused={queueState.paused}>
        <div className="info">
          <span className="dot" />
          <div>
            <div className="label">
              Nurture pipeline: {queueState.paused ? "PAUSED" : "RUNNING"}
            </div>
            <div className="sub">
              {queueState.paused
                ? "All D1–D30 sends are held. Already-scheduled jobs stay queued; nothing leaves until you resume."
                : "Messages send as their scheduled time arrives (D1 immediate, D3 after 2d, etc.)."}
            </div>
          </div>
          {queueState.counts && (
            <div className="counts">
              waiting: {queueState.counts.waiting ?? 0} · delayed: {queueState.counts.delayed ?? 0} · active: {queueState.counts.active ?? 0} · failed: {queueState.counts.failed ?? 0}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchQueueState}
            loading={queueLoading}
            size="small"
          >
            Refresh
          </Button>
          <Popconfirm
            title={queueState.paused ? "Resume nurture pipeline?" : "Pause nurture pipeline?"}
            description={
              queueState.paused
                ? "Workers will start picking up scheduled messages again. Anything overdue fires immediately."
                : "Nothing will be sent until you resume. Already-scheduled D1–D30 jobs stay in the queue."
            }
            onConfirm={handleTogglePause}
            okText={queueState.paused ? "Resume" : "Pause"}
            cancelText="Cancel"
            okButtonProps={{ danger: !queueState.paused }}
          >
            <Button
              type="primary"
              danger={!queueState.paused}
              icon={queueState.paused ? <PlayCircleOutlined /> : <PauseCircleOutlined />}
              loading={queueBusy}
              size="small"
            >
              {queueState.paused ? "Resume sending" : "Pause sending"}
            </Button>
          </Popconfirm>
        </div>
      </QueueBanner>

      <Filters>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search by lead name, phone, leadId..."
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          allowClear
        />
        <Select
          placeholder="Step"
          allowClear
          value={filters.stepDay}
          onChange={(v) => setFilters({ ...filters, stepDay: v })}
          options={[1, 3, 5, 7, 10, 15, 30].map((d) => ({ value: d, label: `D${d}` }))}
        />
        <Select
          placeholder="Status"
          allowClear
          value={filters.status}
          onChange={(v) => setFilters({ ...filters, status: v })}
          options={["queued", "sent", "delivered", "read", "clicked", "failed", "skipped"].map((s) => ({ value: s, label: s }))}
        />
      </Filters>

      {error && <Alert type="error" message={error} style={{ marginBottom: 12 }} />}

      <TableCard>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={rows}
            rowKey={(r) => r.messageId || r._id}
            pagination={{ pageSize: 50, showSizeChanger: true }}
            scroll={{ x: 1400 }}
            locale={{
              emptyText: <Empty description="No messages yet — they'll appear here once Phase 3 starts sending" />,
            }}
          />
        </Spin>
      </TableCard>
    </Page>
  );
}
